import { FEEDBACK_ORIGINS } from "@afar-research/contracts";
import { lazy, Suspense, useEffect } from "react";
import { create } from "zustand";

import { readLocalApi } from "../../localApi";
import type { ReportedFailure } from "@afar-research/contracts";

/**
 * The feedback dialog's open state and its opener: everything the root shell and the entry points
 * import. The form itself loads on first open, so a shell that never opens it never pays for it.
 *
 * `hidden` is a window capture in progress: the dialog is unmounted outright rather than animated
 * closed, so the window it covers is what gets captured. The draft survives in the draft store.
 */
const useFeedbackDialog = create<{
  readonly open: boolean;
  readonly hidden: boolean;
  /** A failure to prefill from, applied by the form once it has loaded (ADR-0138). */
  readonly pendingFailure: ReportedFailure | null;
}>(() => ({ open: false, hidden: false, pendingFailure: null }));

/**
 * Whether this client has any way to send feedback. Entry points are offered only where it does;
 * signed out still counts, and the dialog explains signing in.
 */
export const canSendFeedback = () => readLocalApi()?.feedback !== undefined;

/**
 * Opens the feedback dialog over whatever is on screen. Every entry point calls this. During a
 * window capture it waits for the capture to finish rather than appearing in it.
 */
export function openFeedbackDialog() {
  useFeedbackDialog.setState((state) => (state.hidden ? state : { open: true, hidden: false }));
}

/** "Report this problem": opens the dialog prefilled from a failure the user just saw. */
export function reportFeedbackProblem(failure: ReportedFailure) {
  useFeedbackDialog.setState((state) =>
    state.hidden ? state : { open: true, hidden: false, pendingFailure: failure },
  );
}

export const usePendingFeedbackFailure = () => useFeedbackDialog((state) => state.pendingFailure);

export function clearPendingFeedbackFailure() {
  useFeedbackDialog.setState({ pendingFailure: null });
}

/**
 * The crash screen's "Reload and report": the failed tree cannot host the dialog, so the failure
 * waits in this tab's session storage across one reload, then opens the dialog prefilled. If
 * storage is unavailable the app still reloads; the report is simply not prefilled.
 */
const REPORT_AFTER_RELOAD_KEY = "afar-research:report-after-reload";

export function reloadAndReport(failure: ReportedFailure) {
  try {
    window.sessionStorage.setItem(REPORT_AFTER_RELOAD_KEY, JSON.stringify(failure));
  } catch {
    // Reloading still recovers the app; only the prefill is lost.
  }
  window.location.reload();
}

function takeReportAfterReload(): ReportedFailure | null {
  try {
    const raw = window.sessionStorage.getItem(REPORT_AFTER_RELOAD_KEY);
    if (raw === null) return null;
    window.sessionStorage.removeItem(REPORT_AFTER_RELOAD_KEY);
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { origin, summary, message, occurredAt } = parsed as Record<string, unknown>;
    const knownOrigin = FEEDBACK_ORIGINS.find((candidate) => candidate === origin);
    if (!knownOrigin || typeof summary !== "string" || typeof message !== "string") return null;
    return {
      origin: knownOrigin,
      summary,
      message,
      ...(typeof occurredAt === "string" ? { occurredAt } : {}),
    };
  } catch {
    return null;
  }
}

export function closeFeedbackDialog() {
  useFeedbackDialog.setState({ open: false, hidden: false, pendingFailure: null });
}

export function setFeedbackDialogHidden(hidden: boolean) {
  useFeedbackDialog.setState({ hidden });
}

const FeedbackDialogContent = lazy(() => import("./FeedbackDialogContent"));

/** Mounted once at the root, so the dialog opens over any view without navigating. */
export function FeedbackDialogHost() {
  const { open, hidden } = useFeedbackDialog();
  useEffect(() => {
    const failure = takeReportAfterReload();
    if (failure) reportFeedbackProblem(failure);
  }, []);
  if (!open || hidden) return null;
  return (
    <Suspense fallback={null}>
      <FeedbackDialogContent />
    </Suspense>
  );
}
